import React from 'react';

import {
    BLOCK_COLUMN,
    BLOCK_HINT,
    BLOCK_IMAGES,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_QUOTE,
    BLOCK_TABS,
    conflicts,
    LIST_BLOCKS,
} from '@gitbook/doc-core';
import { Block, type Document, type Fragment } from '@gitbook/slate';
import type { List } from '@gitbook/util-immutable';

import {
    DIFF_LINE_LEFT_DEFAULT,
    DIFF_LINE_LEFT_LIST_TASKS,
    DIFF_LINE_LEFT_NESTED_LIST_TASKS,
    DIFF_LINE_LEFT_NESTED_OL,
    DIFF_LINE_LEFT_NESTED_UL,
    DIFF_LINE_LEFT_OL,
    DIFF_LINE_LEFT_UL,
    getBlockSpacing,
    useParentSkippingDiff,
} from '.';

export const DIFF_LINE_COLUMNS_OFFSET = 44;
export const DIFF_LINE_HINT_OFFSET = 42; /* Size of content padding in hint */
export const DIFF_LINE_QUOTE_OFFSET = 36; /* Size of content padding in quote */
/**
 * Defines the layout (styles, classnames) for the diff block.
 */
export function useDiffBlockLayout({
    ancestors,
    node,
}: {
    ancestors: List<Block | Document | Fragment>;
    node: Block;
}) {
    // Find the parent of the node, ignoring diff
    const parent = useParentSkippingDiff(ancestors);

    const isAtRoot = parent.object === 'document';

    const isConflict = isAtRoot && conflicts.isConflictKey(node.key);

    const blockSpacing = React.useMemo(() => {
        const blockSpacing = getBlockSpacing(node, parent);
        if (isConflict) {
            blockSpacing.paddingTop += 16;
        }
        return blockSpacing;
    }, [parent, node, isConflict]);

    return {
        line: getDiffLineCSS({ node, parent, ancestors, blockSpacing }),
        icon: getDiffIconCSS({ parent }),
    };
}

function getDiffLineCSS({
    parent,
    ancestors,
    blockSpacing,
}: {
    node: Block;
    parent: Block | Document | Fragment;
    ancestors: List<Block | Document | Fragment>;
    blockSpacing: ReturnType<typeof getBlockSpacing>;
}) {
    const isLineRenderedBelowNode = parent.type === BLOCK_IMAGES;
    return React.useMemo(
        () =>
            isLineRenderedBelowNode
                ? {
                      style: {
                          bottom: -4,
                          left: 0,
                          right: 0,
                          borderBottom: '4px solid currentColor',
                      },
                  }
                : ({
                      style: {
                          left: getDiffLineLeftPosition(ancestors),
                          top: blockSpacing.paddingTop,
                          bottom: blockSpacing.paddingBottom,
                      },
                  } as React.CSSProperties),
        [isLineRenderedBelowNode, ancestors, blockSpacing.paddingBottom, blockSpacing.paddingTop]
    );
}

function getDiffIconCSS({ parent }: { parent: Block | Document | Fragment }) {
    const isLineRenderedBelowNode = parent.type === BLOCK_IMAGES;
    return isLineRenderedBelowNode
        ? {
              className: 'left-0.5 top-1',
          }
        : {
              className: 'top-0.5 left-2',
          };
}

/**
 * Compute the X position of the diff line based on the ancestors.
 */
function getDiffLineLeftPosition(ancestors: List<Block | Document | Fragment>): number {
    const parent = ancestors.last() as Block | Document | Fragment;
    const parentParent = ancestors.get(ancestors.size - 2);

    let size = DIFF_LINE_LEFT_DEFAULT;

    if (parent.type === BLOCK_IMAGES) {
        return -4; // Image block has a special diff line position.
    }

    ancestors.forEach((ancestor) => {
        switch (ancestor.type) {
            case BLOCK_TABS:
                size -= 24;
                break;
            case BLOCK_HINT:
                size -= DIFF_LINE_HINT_OFFSET;
                break;
            case BLOCK_QUOTE:
                size -= DIFF_LINE_QUOTE_OFFSET;
                break;
            case BLOCK_COLUMN:
                size += DIFF_LINE_COLUMNS_OFFSET;
                break;
        }
    });

    const blockListAncestors = ancestors.filter(
        (ancestor) => Block.isBlock(ancestor) && LIST_BLOCKS.includes(ancestor.type)
    ).size;

    // Special case for list items
    if (blockListAncestors > 0) {
        // If each individual nested list item is modified,
        // the diff indicator will be calculated for each one separately.
        // For the diff indicator for these elements to display the same as for all others,
        // we need to calculate the offset for each individual element, considering its depth.
        // We don't calculate nested-offset for the root list element, so in this case the depth
        // would equal to zero
        let NODE_DEPTH = blockListAncestors - 1;
        let ancestorType;

        if (LIST_BLOCKS.includes(parentParent?.type || '')) {
            ancestorType = parentParent?.type;
        } else {
            ancestorType = parent.type;

            // If the grandparent is not a list, we need to decrease the depth by one
            NODE_DEPTH--;
        }

        switch (ancestorType) {
            case BLOCK_LIST_UL:
                return size - DIFF_LINE_LEFT_UL - DIFF_LINE_LEFT_NESTED_UL * NODE_DEPTH;
            case BLOCK_LIST_OL:
                return size - DIFF_LINE_LEFT_OL - DIFF_LINE_LEFT_NESTED_OL * NODE_DEPTH;
            case BLOCK_LIST_TASKS:
                return (
                    size - DIFF_LINE_LEFT_LIST_TASKS - DIFF_LINE_LEFT_NESTED_LIST_TASKS * NODE_DEPTH
                );
            default:
                return DIFF_LINE_LEFT_DEFAULT;
        }
    }

    return size;
}
